import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '@pkg/hooks';

import { apiFactory, type AutoConnectWebsocket } from '@app/client';

// TODO: Why is this different from `store > types > WebsocketTopic`?
export enum WebsocketTopicIds {
    CompetitionLineupUpdated = 0,
    CompetitionSubmissionChange = 1,
    FreeYatAssigned = 2,
    ActiveOwners = 3,
    OwnersActivity = 4,
    RankChanges = 5,
    // Old unused topics
    // GemMintedQuantityChanged = 7,
    // GroupActivity = 8,
}

export interface UseSubscribeToTopicOptions {
    id: WebsocketTopicIds;
    topic: string;
    method?: string;
    messageKey: string;
    onmessage: (message: MessageEvent) => void;
}

export function useSubscribeToTopic({
    topic,
    id,
    method = 'TopicSubscribe',
    onmessage,
    messageKey,
}: UseSubscribeToTopicOptions) {
    const user = useAppSelector((state) => state.user?.userClass?.user);
    const [subscribed, setSubscribed] = useState(false);

    const ws = useMemo(() => {
        return apiFactory().websocketConnections.api.connection;
        // Using the `user` to force a refetch of the websocket after toggling from incognito
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, apiFactory()?.websocketConnections.api.connection]);

    const subscribeActivity = useCallback(() => {
        if (ws?.connected) {
            const setTopic = (ws: AutoConnectWebsocket) => {
                const messageBody = { method, id, params: { topic } };
                try {
                    ws.send(JSON.stringify(messageBody));
                    setSubscribed(true);
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Error when sending', err);
                }
            };

            setTopic(ws);
            ws.addOnOpen(topic, setTopic);
            return () => ws.removeOnOpen(topic);
        }
    }, [ws, topic, method, id]);

    useEffect(() => {
        if (!subscribed && ws?.connected) return subscribeActivity();
    }, [topic, subscribeActivity, subscribed, ws?.connected]);

    useEffect(() => {
        if (ws?.connected) {
            ws.addOnMessage(messageKey, onmessage);
            return () => ws.removeOnMessage(messageKey);
        }
    }, [messageKey, onmessage, ws]);
}
